import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  useNavigate,
  useSearchParams
} from 'react-router-dom';

import {
  useAuth
} from '../../contexts/AuthContext';

import {
  supabase
} from '../../services/supabase';

import AppLayout from '../../components/Layout/ParentAccountLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';
import { FcGoogle } from 'react-icons/fc';

const GuardianInvite = () => {
  const navigate = useNavigate();

  const [
    searchParams
  ] = useSearchParams();

  const {
    user,
    parent,
    initializing,
    parentChecked,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    refreshParent
  } = useAuth();

  const inviteToken =
    searchParams.get('token')?.trim() ||
    '';

  const [
    invitation,
    setInvitation
  ] = useState(null);

  const [
    inviteLoading,
    setInviteLoading
  ] = useState(true);

  const [
    inviteError,
    setInviteError
  ] = useState('');

  const [
    mode,
    setMode
  ] = useState('signin');

  const [
    formData,
    setFormData
  ] = useState({
    name: '',
    email: '',
    password: ''
  });

  const [
    authLoading,
    setAuthLoading
  ] = useState(false);

  const [
    googleLoading,
    setGoogleLoading
  ] = useState(false);

  const [
    authError,
    setAuthError
  ] = useState('');

  const [
    signupEmailSent,
    setSignupEmailSent
  ] = useState(false);

  const [
    accepting,
    setAccepting
  ] = useState(false);

  const [
    acceptError,
    setAcceptError
  ] = useState('');

  const [
    guardianName,
    setGuardianName
  ] = useState('');

  /*
   * ---------------------------------------------------------
   * INVITATION RETURN URL
   * ---------------------------------------------------------
   *
   * Supabase email confirmation must return the invited adult
   * to this exact invitation rather than normal parent signup.
   */
  const invitationUrl =
    useMemo(() => {
      if (!inviteToken) {
        return '';
      }

      return (
        `${window.location.origin}` +
        `/parent/invite?token=` +
        encodeURIComponent(
          inviteToken
        )
      );
    }, [
      inviteToken
    ]);

  /*
   * ---------------------------------------------------------
   * RESET GOOGLE LOADING AFTER BROWSER HISTORY RESTORE
   * ---------------------------------------------------------
   *
   * A cancelled OAuth redirect can restore this page from the
   * browser's back-forward cache with React state still set to
   * loading. Reset only when the browser restores that cached
   * page so normal in-progress authentication is unaffected.
   */
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        setGoogleLoading(false);
      }
    };

    window.addEventListener(
      'pageshow',
      handlePageShow
    );

    return () => {
      window.removeEventListener(
        'pageshow',
        handlePageShow
      );
    };
  }, []);

  /*
   * ---------------------------------------------------------
   * INSPECT INVITATION
   * ---------------------------------------------------------
   *
   * This RPC is intentionally available before authentication.
   *
   * Invalid, cancelled, expired and already-used invitations
   * all return zero rows.
   */
  useEffect(() => {
    let active = true;

    const inspectInvitation =
      async () => {
        setInviteLoading(true);
        setInviteError('');

        if (!inviteToken) {
          setInvitation(null);

          setInviteError(
            'This invitation link is invalid or incomplete.'
          );

          setInviteLoading(false);

          return;
        }

        try {
          const {
            data,
            error
          } =
            await supabase.rpc(
              'inspect_guardian_invitation',
              {
                p_invite_token:
                  inviteToken
              }
            );

          if (error) {
            throw error;
          }

          if (!active) {
            return;
          }

          if (
            !Array.isArray(data) ||
            data.length === 0
          ) {
            setInvitation(null);

            setInviteError(
              'This invitation is invalid, expired, cancelled, or has already been used.'
            );

            return;
          }

          const invite =
            data[0];

          setInvitation(invite);

          setFormData(
            (current) => ({
              ...current,
              email:
                invite.invited_email ||
                ''
            })
          );
        } catch (error) {
          console.error(
            'Guardian invitation inspection error:',
            error
          );

          if (!active) {
            return;
          }

          setInvitation(null);

          setInviteError(
            'We could not verify this invitation right now. Please try again.'
          );
        } finally {
          if (active) {
            setInviteLoading(false);
          }
        }
      };

    inspectInvitation();

    return () => {
      active = false;
    };
  }, [
    inviteToken
  ]);

  /*
   * ---------------------------------------------------------
   * EXISTING FAMILY MEMBER
   * ---------------------------------------------------------
   *
   * A signed-in account that already has a MyPiggy parent
   * membership must never consume a Guardian invitation.
   */
  useEffect(() => {
    if (initializing) return;
    if (!parentChecked) return;

    if (
      user &&
      parent
    ) {
      setAcceptError(
        'This signed-in account already belongs to a MyPiggy family. Sign out before accepting an invitation for another account.'
      );
    }
  }, [
    user,
    parent,
    initializing,
    parentChecked
  ]);

  /*
   * ---------------------------------------------------------
   * GOOGLE DISPLAY-NAME SUGGESTION
   * ---------------------------------------------------------
   *
   * Google normally provides the parent's name in user
   * metadata. Suggest it, but allow the Guardian to edit it
   * before accepting the invitation.
   */
  useEffect(() => {
    if (
      !user ||
      guardianName.trim()
    ) {
      return;
    }

    const suggestedName =
      (
        user.user_metadata
          ?.full_name ||
        user.user_metadata
          ?.name ||
        ''
      ).trim();

    if (suggestedName) {
      setGuardianName(
        suggestedName
      );
    }
  }, [
    user,
    guardianName,
  ]);

  /*
   * ---------------------------------------------------------
   * FORM CHANGES
   * ---------------------------------------------------------
   */
  const handleChange = (event) => {
    const {
      name,
      value
    } = event.target;

    setFormData(
      (current) => ({
        ...current,
        [name]: value
      })
    );

    setAuthError('');
    setAcceptError('');
  };

  /*
   * ---------------------------------------------------------
   * SIGN IN / CREATE ACCOUNT
   * ---------------------------------------------------------
   */
  const handleAuthSubmit =
    async (event) => {
      event.preventDefault();

      if (
        authLoading ||
        googleLoading ||
        !invitation
      ) {
        return;
      }

      setAuthLoading(true);
      setAuthError('');
      setAcceptError('');

      try {
        const invitedEmail =
          invitation
            .invited_email
            ?.trim()
            .toLowerCase();

        const enteredEmail =
          formData.email
            .trim()
            .toLowerCase();

        if (
          enteredEmail !==
          invitedEmail
        ) {
          throw new Error(
            'Please use the email address that received this invitation.'
          );
        }

        if (mode === 'signup') {
          const parentName =
            formData.name.trim();

          if (!parentName) {
            throw new Error(
              'Please enter your name.'
            );
          }

          const result =
            await signUp(
              invitedEmail,
              formData.password,
              parentName,
              {
                emailRedirectTo:
                  invitationUrl
              }
            );

          if (
            result
              ?.needsEmailConfirmation
          ) {
            setSignupEmailSent(
              true
            );

            setFormData(
              (current) => ({
                ...current,
                password: ''
              })
            );

            return;
          }
        } else {
          await signIn(
            invitedEmail,
            formData.password
          );
        }
      } catch (error) {
        console.error(
          mode === 'signup'
            ? 'Guardian signup error:'
            : 'Guardian sign-in error:',
          error
        );

        setAuthError(
          error?.message ||
            'Unable to continue. Please try again.'
        );
      } finally {
        setAuthLoading(false);
      }
    };
    /*
   * ---------------------------------------------------------
   * GUARDIAN GOOGLE AUTHENTICATION
   * ---------------------------------------------------------
   */
  const handleGoogleSignIn =
    async () => {
      if (
        authLoading ||
        googleLoading ||
        !invitation ||
        !inviteToken
      ) {
        return;
      }

      setGoogleLoading(true);
      setAuthError('');
      setAcceptError('');

      try {
        await signInWithGoogle({
          flow:
            'guardian_invitation',

          inviteToken,
        });
      } catch (googleError) {
        console.error(
          'Guardian Google authentication error:',
          googleError
        );

        setAuthError(
          googleError?.message ||
            'Google sign-in could not be started. Please try again.'
        );

        setGoogleLoading(false);
      }
    };

  const isAuthSubmitting =
    authLoading ||
    googleLoading;

  /*
   * ---------------------------------------------------------
   * ACCEPT INVITATION
   * ---------------------------------------------------------
   */
  const handleAccept =
    async () => {
      if (
        accepting ||
        !user ||
        parent ||
        !invitation
      ) {
        return;
      }

      setAccepting(true);
      setAcceptError('');

      try {
        const authenticatedEmail =
          user.email
            ?.trim()
            .toLowerCase();

        const invitedEmail =
          invitation
            .invited_email
            ?.trim()
            .toLowerCase();

        if (
          authenticatedEmail !==
          invitedEmail
        ) {
          throw new Error(
            'Please sign in with the email address that received this invitation.'
          );
        }

        const parentName =
          guardianName.trim();

        if (!parentName) {
          throw new Error(
            'Please enter your name before accepting the invitation.'
          );
        }

        const {
          data,
          error
        } =
          await supabase.rpc(
            'accept_guardian_invitation',
            {
              p_invite_token:
                inviteToken,

              p_parent_name:
                parentName
            }
          );

        if (error) {
          throw error;
        }

        if (
          !Array.isArray(data) ||
          data.length === 0
        ) {
          throw new Error(
            'The invitation was accepted, but no membership result was returned.'
          );
        }

        /*
         * Reload AuthContext so ProtectedParentRoute sees the
         * newly created Guardian membership.
         */
        const parentData =
          await refreshParent({
            blockUI: true
          });

        if (!parentData) {
          throw new Error(
            'Your invitation was accepted, but your family profile could not be loaded. Please sign out and sign back in.'
          );
        }

        navigate(
          '/parent',
          {
            replace: true
          }
        );
      } catch (error) {
        console.error(
          'Guardian invitation acceptance error:',
          error
        );

        setAcceptError(
          error?.message ||
            'Unable to accept this invitation. Please try again.'
        );
      } finally {
        setAccepting(false);
      }
    };

  /*
   * ---------------------------------------------------------
   * SIGN OUT WRONG / EXISTING ACCOUNT
   * ---------------------------------------------------------
   */
  const handleSignOut =
    async () => {
      try {
        await signOut();

        setAuthError('');
        setAcceptError('');
        setSignupEmailSent(false);
        setGuardianName('');
      } catch (error) {
        console.error(
          'Guardian invitation sign out error:',
          error
        );

        setAcceptError(
          error?.message ||
            'Unable to sign out. Please try again.'
        );
      }
    };

  /*
   * ---------------------------------------------------------
   * LOADING
   * ---------------------------------------------------------
   */
  if (
    inviteLoading ||
    initializing
  ) {
    return (
      <AppLayout title="MyPiggy">
        <div className="max-w-lg mx-auto">
          <Card className="p-4 sm:p-5 text-center">
            <p className="text-gray-600 dark:text-slate-300">
              Checking your invitation...
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  /*
   * ---------------------------------------------------------
   * INVALID / UNAVAILABLE INVITATION
   * ---------------------------------------------------------
   */
  if (
    inviteError ||
    !invitation
  ) {
    return (
      <AppLayout title="MyPiggy">
        <div className="max-w-lg mx-auto">
          <Card className="p-4 sm:p-5 text-center">

            <MyPiggyIcon name="help" size="lg" className="mb-4" />

            <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-slate-100 mb-3">
              Invitation unavailable
            </h2>

            <p className="text-gray-600 dark:text-slate-300 mb-4">
              {inviteError}
            </p>

            <Button
              type="button"
              className="w-full"
              onClick={() =>
                navigate('/parent/login')
              }
            >
              Go to MyPiggy Sign In
            </Button>

          </Card>
        </div>
      </AppLayout>
    );
  }

  /*
   * ---------------------------------------------------------
   * EMAIL CONFIRMATION NOTICE
   * ---------------------------------------------------------
   */
  if (
    signupEmailSent &&
    !user
  ) {
    return (
      <AppLayout title="MyPiggy">
        <div className="max-w-lg mx-auto">
          <Card className="p-4 sm:p-5 text-center">

            <MyPiggyIcon name="my-piggy-bank" size="lg" className="mb-4" />

            <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-slate-100 mb-3">
              Check your email
            </h2>

            <p className="text-gray-600 dark:text-slate-300 mb-4">
              We sent a confirmation link to:
            </p>

            <p className="font-semibold text-gray-900 dark:text-slate-100 break-all mb-5">
              {invitation.invited_email}
            </p>

            <p className="text-sm text-gray-500 dark:text-slate-300">
              After confirming your email, you'll return to this invitation to join the family.
            </p>

          </Card>
        </div>
      </AppLayout>
    );
  }

  /*
   * ---------------------------------------------------------
   * AUTHENTICATED USER
   * ---------------------------------------------------------
   */
  if (user) {
    const authenticatedEmail =
      user.email
        ?.trim()
        .toLowerCase();

    const invitedEmail =
      invitation
        .invited_email
        ?.trim()
        .toLowerCase();

    const emailMatches =
      authenticatedEmail ===
      invitedEmail;

    return (
      <AppLayout title="MyPiggy">
        <div className="max-w-lg mx-auto">

          <Card className="p-4 sm:p-5">

            <div className="text-center mb-4">
              <MyPiggyIcon name="family" size="lg" className="mb-4" />

              <h2 className="text-2xl font-extrabold text-gray-900 dark:text-slate-100 mb-3">
                Join this MyPiggy family
              </h2>

              <p className="text-gray-600 dark:text-slate-300">
                You've been invited as a Parent/Guardian.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4 mb-5">

              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-slate-300 font-medium">
                Invited by
              </p>

              <p className="font-semibold text-gray-900 dark:text-slate-100 mt-1">
                {invitation.invited_by_name ||
                  'MyPiggy Family Organizer'}
              </p>

              {invitation.invited_by_email && (
                <p className="text-sm text-gray-600 dark:text-slate-300 break-all mt-1">
                  {invitation.invited_by_email}
                </p>
              )}

              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-slate-300 font-medium mt-4">
                Signed in as
              </p>

              <p className="font-semibold text-gray-900 dark:text-slate-100 break-all mt-1">
                {user.email}
              </p>

            </div>

            {emailMatches &&
              !parent && (
                <div className="mb-5">
                  <label
                    htmlFor="guardian-name"
                    className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
                  >
                    Your name
                  </label>

                  <Input
                    id="guardian-name"
                    type="text"
                    name="guardianName"
                    placeholder="Your name"
                    value={guardianName}
                    onChange={(event) => {
                      setGuardianName(
                        event.target.value
                      );

                      setAcceptError('');
                    }}
                    required
                  />

                  <p className="text-xs text-gray-500 dark:text-slate-300 mt-2">
                    This name will be shown inside the family's MyPiggy account.
                  </p>
                </div>
              )}

            {!emailMatches && (
              <div
                className="text-red-700 dark:text-red-300 text-sm bg-red-50 dark:bg-red-950/35 border border-red-100 dark:border-red-900 p-3 rounded-lg mb-5"
                role="alert"
              >
                This invitation belongs to a different email address. Please sign out and use the invited account.
              </div>
            )}

            {acceptError && (
              <div
                className="text-red-700 dark:text-red-300 text-sm bg-red-50 dark:bg-red-950/35 border border-red-100 dark:border-red-900 p-3 rounded-lg mb-5"
                role="alert"
              >
                {acceptError}
              </div>
            )}

            {emailMatches &&
              !parent && (
                <Button
                  type="button"
                  className="w-full"
                  onClick={
                    handleAccept
                  }
                  disabled={
                    accepting ||
                    !guardianName.trim()
                  }
                >
                  {accepting
                    ? 'Joining family...'
                    : 'Accept Invitation'}
                </Button>
              )}

            <button
              type="button"
              className="w-full mt-4 text-gray-500 dark:text-slate-300 hover:text-gray-800 dark:hover:text-slate-200 text-sm transition-colors"
              onClick={
                handleSignOut
              }
              disabled={
                accepting
              }
            >
              Sign out
            </button>

          </Card>
        </div>
      </AppLayout>
    );
  }

  /*
   * ---------------------------------------------------------
   * SIGN IN / SIGN UP
   * ---------------------------------------------------------
   */
  return (
    <AppLayout title="MyPiggy">
      <div className="max-w-lg mx-auto">

        <Card className="p-4 sm:p-5">

          <div className="text-center mb-4">
            <MyPiggyIcon name="family" size="lg" className="mb-4" />

            <h2 className="text-2xl font-extrabold text-gray-900 dark:text-slate-100 mb-3">
              Join a MyPiggy family
            </h2>

            <p className="text-gray-600 dark:text-slate-300">
              You've been invited as a Parent/Guardian.
            </p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-950/35 border border-blue-100 dark:border-blue-900 rounded-xl p-4 mb-4">

            <p className="text-sm text-gray-600 dark:text-slate-300">
              You've been invited by
            </p>

            <p className="font-semibold text-gray-900 dark:text-slate-100 mt-1">
              {invitation.invited_by_name ||
                'a MyPiggy Family Organizer'}
            </p>

            {invitation.invited_by_email && (
              <p className="text-sm text-gray-600 dark:text-slate-300 break-all mt-1">
                {invitation.invited_by_email}
              </p>
            )}

            <div className="mt-4 pt-4 border-t border-blue-100 dark:border-blue-900">

              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-slate-300 font-medium">
                Invitation sent to
              </p>

              <p className="font-semibold text-gray-900 dark:text-slate-100 break-all mt-1">
                {invitation.invited_email}
              </p>

            </div>

          </div>

          <button
            type="button"
            onClick={
              handleGoogleSignIn
            }
            disabled={
              isAuthSubmitting
            }
            className="w-full flex items-center justify-center gap-3 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-3 font-semibold text-gray-800 dark:text-slate-100 shadow-sm transition hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FcGoogle
              className="text-2xl shrink-0"
              aria-hidden="true"
            />

            <span>
              {googleLoading
                ? 'Connecting to Google...'
                : 'Continue with Google'}
            </span>
          </button>

          <div className="relative my-6">
            <div
              className="absolute inset-0 flex items-center"
              aria-hidden="true"
            >
              <div className="w-full border-t border-gray-200 dark:border-slate-700" />
            </div>

            <div className="relative flex justify-center">
              <span className="bg-white dark:bg-slate-900 px-3 text-sm text-gray-500 dark:text-slate-300">
                or use email
              </span>
            </div>
          </div>

          <div className="flex rounded-xl bg-gray-100 dark:bg-slate-800 p-1 mb-4">
            <button
              type="button"
              disabled={isAuthSubmitting}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${
                mode === 'signin'
                  ? 'bg-white dark:bg-slate-700 shadow text-gray-900 dark:text-white'
                  : 'text-gray-600 dark:text-slate-300'
              }`}
              onClick={() => {
                setMode('signin');
                setAuthError('');
              }}
            >
              Sign In
            </button>

            <button
              type="button"
              disabled={isAuthSubmitting}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${
                mode === 'signup'
                  ? 'bg-white dark:bg-slate-700 shadow text-gray-900 dark:text-white'
                  : 'text-gray-600 dark:text-slate-300'
              }`}
              onClick={() => {
                setMode('signup');
                setAuthError('');
              }}
            >
              Create Account
            </button>
          </div>
          <form
            onSubmit={
              handleAuthSubmit
            }
            className="space-y-4"
          >

            {mode === 'signup' && (
              <Input
                type="text"
                name="name"
                placeholder="Your name"
                value={formData.name}
                onChange={
                  handleChange
                }
                required
              />
            )}

            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={
                handleChange
              }
              required
              readOnly
            />

            <Input
              type="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={
                handleChange
              }
              required
            />

            {authError && (
              <div
                className="text-red-700 dark:text-red-300 text-sm bg-red-50 dark:bg-red-950/35 border border-red-100 dark:border-red-900 p-3 rounded-lg"
                role="alert"
              >
                {authError}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isAuthSubmitting}
            >
              {authLoading
                ? 'Please wait...'
                : mode ===
                    'signup'
                  ? 'Create Guardian Account'
                  : 'Sign In to Continue'}
            </Button>

          </form>

          <p className="text-xs text-gray-500 dark:text-slate-300 text-center mt-5">
            The email address is locked to the address that received this invitation.
          </p>

        </Card>
      </div>
    </AppLayout>
  );
};

export default GuardianInvite;

